import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { Receipt } from 'lucide-react';
import Popup from '../../../../components/common-components/pop-up/Popup';
import FormPopupFooter from '../../../../components/form-components/common-components/FormPopupFooter';
import { useStore } from '../../../../hooks/useStore';
import { EditExpenseFormSchema } from '../../../../schemas/ExpenseFormSchema';
import type { IExpenseFileResult } from '../../../../models/expense/ExpenseModel';
import { toast } from 'react-toastify';
import ExpenseFormFields from './ExpenseFormFields';
import {
  buildExpenseFormData,
  getEditExpenseFormValues,
  type ExpenseFormValues,
} from './expenseFormUtils';
import styles from './NewExpensePopup.module.css';

const EditExpensePopup = () => {
  const { popupStore, expenseStore, authStore } = useStore();
  const [deletedFileIds, setDeletedFileIds] = useState<number[]>([]);
  const [existingFiles, setExistingFiles] = useState<IExpenseFileResult[]>([]);
  const selectedExpense = expenseStore.selectedExpense;

  useEffect(() => {
    if (selectedExpense) {
      setExistingFiles(selectedExpense.files || []);
      setDeletedFileIds([]);
    }
  }, [selectedExpense]);

  const handleClose = () => {
    popupStore.setIsEditExpenseOpen(false);
    expenseStore.setSelectedExpense(null);
    setDeletedFileIds([]);
    setExistingFiles([]);
  };

  const handleSubmit = async (values: ExpenseFormValues) => {
    if (!selectedExpense) {
      return;
    }

    const formData = buildExpenseFormData(values, {
      deletedFileIds,
      includeStatus: authStore.hasAnyRole(['Admin']),
    });

    if (!formData) {
      toast.error('Geçerli bir miktar giriniz');
      return;
    }

    const response = await expenseStore.updateExpense(selectedExpense.id, formData);
    if (!response?.error) {
      handleClose();
    }
  };

  const handleRemoveExistingFile = (fileId: number) => {
    setDeletedFileIds(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  if (!popupStore.isEditExpenseOpen || !selectedExpense) {
    return null;
  }

  const isAdmin = authStore.hasAnyRole(['Admin']);

  return (
    <Formik
      initialValues={getEditExpenseFormValues(selectedExpense)}
      validationSchema={EditExpenseFormSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {() => (
        <Popup
          isOpen={popupStore.isEditExpenseOpen}
          onClose={handleClose}
          title="Harcama Kaydını Düzenle"
          description="Harcama kaydını güncelleyin"
          variant="success"
          icon={<Receipt size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter
              onClose={handleClose}
              saveLabel="Güncelle"
              submittingLabel="Güncelleniyor..."
            />
          )}
        >
          <Form className={styles.form}>
            <ExpenseFormFields
              showStatus={isAdmin}
              existingFiles={existingFiles}
              onRemoveExistingFile={handleRemoveExistingFile}
              fileUploadLabel="Yeni Dosyalar Ekle (İsteğe Bağlı)"
            />
          </Form>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditExpensePopup);
